// Scoped localStorage for ToneGuard — transparently namespaces every
// `toneguard:*` key under the logged-in user. Keeps the existing storage
// modules (chats.js, sessionLog.js, toneMirrorLayers.js, layer1Display.js,
// etc.) untouched while preventing cross-user data leakage on a shared
// browser.
//
// Install once at app boot via `installScopedStorage()`; set the active
// scope on login via `setStorageScope(user_id)`. While the scope is null
// (anonymous mode / before login), keys pass through unchanged so legacy
// data is still readable.
//
// In addition to scoping, the wrapper invokes an optional WRITE HOOK on
// every setItem / removeItem of a `toneguard:*` key. cloudSync.js
// registers itself as that hook so every local edit also fires a
// debounced PUT to /api/user-state.

const PREFIX = "toneguard:";

let _userId = null;
let _installed = false;
let _orig = null;
let _writeHook = null;

function scoped(key) {
    if (!_userId) return key;
    if (typeof key !== "string" || !key.startsWith(PREFIX)) return key;
    return `${key}:u:${_userId}`;
}

function fireWriteHook(key) {
    if (!_writeHook) return;
    if (typeof key !== "string" || !key.startsWith(PREFIX)) return;
    try {
        _writeHook(key);
    } catch {
        /* hooks must never break storage writes */
    }
}

export function installScopedStorage() {
    if (_installed || typeof window === "undefined" || !window.localStorage) return;
    const ls = window.localStorage;
    _orig = {
        getItem: ls.getItem.bind(ls),
        setItem: ls.setItem.bind(ls),
        removeItem: ls.removeItem.bind(ls),
        key: ls.key.bind(ls),
        // `length` is a getter on Storage — wrap so callers can read it
        // through __originalStorage() without re-implementing.
        get length() {
            return ls.length;
        },
    };
    ls.getItem = (k) => _orig.getItem(scoped(k));
    ls.setItem = (k, v) => {
        _orig.setItem(scoped(k), v);
        fireWriteHook(k);
    };
    ls.removeItem = (k) => {
        _orig.removeItem(scoped(k));
        fireWriteHook(k);
    };
    _installed = true;
}

export function setStorageScope(userId) {
    _userId = userId || null;
}

export function getStorageScope() {
    return _userId;
}

/**
 * Register a function to be called on every write to a `toneguard:*`
 * key. Pass `null` to unregister. The hook receives the UNSCOPED key
 * name. Exceptions inside the hook are swallowed.
 */
export function setStorageWriteHook(hook) {
    _writeHook = typeof hook === "function" ? hook : null;
}

/**
 * Expose the UNSCOPED localStorage so cloudSync can enumerate every
 * scoped key for the active user. Returns a thin wrapper exposing the
 * Storage API methods we actually use; the wrapper is bound to the same
 * `window.localStorage` object so all reads/writes hit the underlying
 * store directly (bypassing the scoping done by `setItem` / `getItem`).
 */
export function __originalStorage() {
    if (!_orig) {
        // Not installed yet — fall back to the raw localStorage so
        // anything called during boot still works.
        if (typeof window !== "undefined" && window.localStorage) {
            return window.localStorage;
        }
    }
    return _orig;
}
